import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from '../api/directMessages';

const dmKeys = {
  conversation: (otherUserId: string) => ['dm', 'conversation', otherUserId] as const,
  members: (conversationId: string) => ['dm', 'members', conversationId] as const,
  messages: (conversationId: string) => ['dm', 'messages', conversationId] as const,
  reactions: (conversationId: string, len: number, last: string) =>
    ['dm', 'reactions', conversationId, len, last] as const,
};

export function useDirectConversation(otherUserId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: dmKeys.conversation(otherUserId ?? ''),
    queryFn: () => api.getOrCreateDm(otherUserId!),
    enabled: enabled && Boolean(otherUserId),
  });
}

export function useDirectMembers(conversationId: string | undefined) {
  return useQuery({
    queryKey: dmKeys.members(conversationId ?? ''),
    queryFn: () => api.getConversationMembers(conversationId!),
    enabled: Boolean(conversationId),
  });
}

export function useDirectPeer(conversationId: string | undefined, currentUserId: string | undefined) {
  return useQuery({
    queryKey: ['dm', 'peer', conversationId ?? '', currentUserId ?? ''],
    queryFn: () => api.getConversationPeer(conversationId!, currentUserId!),
    enabled: Boolean(conversationId) && Boolean(currentUserId),
  });
}

export function useDirectMessages(conversationId: string | undefined) {
  return useQuery({
    queryKey: dmKeys.messages(conversationId ?? ''),
    queryFn: () => api.listMessages(conversationId!),
    enabled: Boolean(conversationId),
    refetchInterval: 30000,
  });
}

/** Search the signed-in user's chat history by message text. */
export function useMessageSearch(userId: string | undefined, query: string) {
  return useQuery({
    queryKey: ['dm', 'search', userId ?? '', query.trim()],
    queryFn: () => api.searchMyMessages(userId!, query),
    enabled: Boolean(userId) && query.trim().length >= 2,
  });
}

export function useDirectReactions(conversationId: string | undefined, messageIds: string[]) {
  return useQuery({
    queryKey: dmKeys.reactions(conversationId ?? '', messageIds.length, messageIds[messageIds.length - 1] ?? ''),
    queryFn: () => api.listReactions(messageIds),
    enabled: Boolean(conversationId) && messageIds.length > 0,
    refetchInterval: 30000,
  });
}

export function useSendDirectMessage(conversationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.sendMessage,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dm', 'messages', conversationId] }),
  });
}

export function useEditDirectMessage(conversationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) => api.editMessage(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dm', 'messages', conversationId] }),
  });
}

export function useDeleteDirectMessage(conversationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteMessage(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dm', 'messages', conversationId] }),
  });
}

export function useToggleDirectReaction(conversationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId, userId, emoji }: { messageId: string; userId: string; emoji: string }) =>
      api.toggleReaction(messageId, userId, emoji),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dm', 'reactions', conversationId] }),
  });
}
